"use client";

import React, {useState, useRef} from "react";
import { apiService } from "@/services/api";
import type { AutocompleteResult } from "@/services/api";
import ConfirmModal from '../Common/ConfirmModel'
import { motion, AnimatePresence } from 'framer-motion';
import {
	X,
	Search,
	Users,
	User,
	ChevronRight,
	AlertCircle,
	Loader2,
} from 'lucide-react';

interface GroupMember {
	id: number;
	first_name: string;
	last_name: string;
	rsvp_status?: string;
	message?: string;
	attending_pre_event?: string;
	is_child?: boolean | number;
}

type RSVP = 'confirmed' | 'declined';
type YesNo = 'yes' | 'no';

interface GroupRSVPMember {
	id: number;
	rsvp_status: RSVP;
	message: string | null;
	attending_pre_event: YesNo;
}

interface GroupRSVPData {
	decision_maker_id: number;
	group_id: number;
	members: GroupRSVPMember[];
}

const RSVPForm: React.FC = () => {
	const [query, setQuery] = useState("");
	const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([]);
	const [selectedGuest, setSelectedGuest] = useState<AutocompleteResult | null>(null);
	const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
	const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [showGroupForm, setShowGroupForm] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const [groupType, setGroupType] = useState<string | undefined>(undefined);
	const [groupMode, setGroupMode] = useState<'none' | 'grouped' | 'detailed'>('none');
	const [childrenPresent, setChildrenPresent] = useState<'yes' | 'no' | ''>('');
	const [groupMessage, setGroupMessage] = useState('');
	const [groupPresence, setGroupPresence] = useState<'confirmed' | 'declined' | ''>('');
	const [groupPreEvent, setGroupPreEvent] = useState<'yes' | 'no' | ''>('');
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [selectedMemberIndex, setSelectedMemberIndex] = useState<number | null>(0);

	// Recherche à chaque frappe
	const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setQuery(value);
		setSelectedGuest(null);
		setShowSuggestions(true);
		setGroupMembers([]);
		setCurrentStep(1);

		if (value.length < 2) {
			setSuggestions([]);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		try {
			const res = await apiService.searchGuests(value);
			if (Array.isArray(res)) {
				setSuggestions(res);
			} else if (res && Array.isArray(res.data)) {
				setSuggestions(res.data);
			} else {
				setSuggestions([]);
			}
		} catch {
			setSuggestions([]);
		} finally {
			setIsLoading(false);
		}
	};

// Quand un invité est sélectionné
	const handleSuggestionClick = async (guest: AutocompleteResult) => {
		setSelectedGuest(guest);
		setQuery(`${guest.firstName} ${guest.lastName}`);
		setShowSuggestions(false);
		setShowGroupForm(false);
		setGroupType(guest.group_type);
		setGroupMode('none');
		setChildrenPresent('');
		setGroupMessage('');
		setGroupPresence('');
		setGroupPreEvent('');
		setStatus('idle');
		setCurrentStep(2);
		setSelectedMemberIndex(0);

		if (guest.group_id) {
			try {
				const res = await apiService.getGuestsByGroupId(guest.group_id);
				if (res.success && Array.isArray(res.data) && res.data.length > 1) {
					// Mapper toutes les données
					const allMembers = res.data.map((m: any) => ({
						id: m.id,
						first_name: m.first_name,
						last_name: m.last_name,
						rsvp_status: m.rsvp_status || "pending", // ✅ CORRECTION : valeur par défaut
						message: m.message || "",
						attending_pre_event: m.attending_pre_event || "",
						is_child: m.is_child
					}));

					// S'assurer que selectedGuest est en première position (décisionnaire)
					const decisionMaker = allMembers.find(m => m.id === guest.id);
					const otherMembers = allMembers.filter(m => m.id !== guest.id);

					if (decisionMaker) {
						// Le décisionnaire en premier, puis les autres
						setGroupMembers([decisionMaker, ...otherMembers]);
					} else {
						// Si selectedGuest n'est pas dans la liste, l'ajouter en premier
						const selectedAsDecisionMaker = {
							id: guest.id,
							first_name: guest.firstName,
							last_name: guest.lastName,
							rsvp_status: "pending", // ✅ CORRECTION : valeur par défaut
							message: "",
							attending_pre_event: guest.attending_pre_event || "",
							is_child: guest.is_child || false
						};
						setGroupMembers([selectedAsDecisionMaker, ...allMembers]);
					}
				} else {
					setGroupMembers([
						{
							id: guest.id,
							first_name: guest.firstName,
							last_name: guest.lastName,
							rsvp_status: "pending", // ✅ CORRECTION : valeur par défaut
							message: "",
							attending_pre_event: guest.attending_pre_event || "",
							is_child: guest.is_child
						}
					]);
				}
			} catch {
				setGroupMembers([
					{
						id: guest.id,
						first_name: guest.firstName,
						last_name: guest.lastName,
						rsvp_status: "",
						message: "",
						attending_pre_event: guest.attending_pre_event || "",
						is_child: guest.is_child
					}
				]);
			}
		} else {
			setGroupMembers([
				{
					id: guest.id,
					first_name: guest.firstName,
					last_name: guest.lastName,
					rsvp_status: "",
					message: "",
					attending_pre_event: guest.attending_pre_event || "",
					is_child: guest.is_child
				}
			]);
		}
	};

	// Handlers pour les updates
	const updateMemberRSVP = (idx: number, value: string) => {
		setGroupMembers(members =>
			members.map((m, i) => (i === idx ? { ...m, rsvp_status: value } : m))
		);
	};

	const updateMemberMessage = (idx: number, value: string) => {
		setGroupMembers(members =>
			members.map((m, i) => (i === idx ? { ...m, message: value } : m))
		);
	};

	const updateMemberPreEvent = (idx: number, value: string) => {
		setGroupMembers(members =>
			members.map((m, i) => (i === idx ? { ...m, attending_pre_event: value } : m))
		);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setStatus("idle");
		if (!selectedGuest || groupMembers.length === 0) return;

		try {
			const isGrouped =
				(groupType === "family" || groupType === "couple") &&
				groupMode === "grouped";

// ======= CAS GROUPED (réponses individuelles par membre) =======
			if (isGrouped) {
				const groupId = selectedGuest.group_id;
				if (!groupId) {
					alert("Groupe introuvable pour ce contact.");
					setStatus("error");
					return;
				}

				// ✅ VALIDATION : Chaque membre doit avoir sa propre réponse
				const invalidMembers = groupMembers.filter(
					m => !m.rsvp_status || !["confirmed", "declined"].includes(m.rsvp_status)
				);

				if (invalidMembers.length > 0) {
					console.error("❌ Membres sans réponse individuelle valide:", invalidMembers);
					alert("Veuillez répondre individuellement pour tous les membres du groupe.");
					setStatus("error");
					return;
				}

				// ✅ Envoyer les réponses individuelles de chaque membre
				const payload: GroupRSVPData = {
					decision_maker_id: selectedGuest.id,
					group_id: groupId,
					members: groupMembers.map<GroupRSVPMember>(m => ({
						id: m.id,
						rsvp_status: m.rsvp_status as RSVP, // Réponse individuelle de chaque membre
						message: (m.message ?? "").trim() || null, // Message individuel
						attending_pre_event: (m.attending_pre_event === "yes" ? "yes" : "no"), // Choix individuel
					})),
				};

				const resp = await apiService.updateGroupRSVP(payload);
				if (resp?.success) {
					setStatus("success");
					setShowSuccessModal(true);
					resetForm();
				} else {
					setStatus("error");
				}
				return;
			}

			// ======= CAS GROUPE (non grouped) =======
			if (groupMembers.length > 1) {
				const groupId = selectedGuest.group_id;
				if (!groupId) {
					alert("Groupe introuvable pour ce contact.");
					setStatus("error");
					return;
				}

				const invalidMembers = groupMembers.filter(
					m => !m.rsvp_status || !["confirmed", "declined"].includes(m.rsvp_status)
				);
				if (invalidMembers.length > 0) {
					console.error("❌ Membres sans réponse valide:", invalidMembers);
					alert("Veuillez répondre pour tous les membres du groupe avant de continuer.");
					setStatus("error");
					return;
				}

				const resp = await apiService.updateGroupRSVP({
					decision_maker_id: selectedGuest.id,
					group_id: groupId, // ✅ plus de undefined
					members: groupMembers.map<GroupRSVPMember>(m => ({
						id: m.id,
						rsvp_status: m.rsvp_status as RSVP,
						attending_pre_event: (m.attending_pre_event === "yes" ? "yes" : "no"),
						message: (m.message ?? "").trim() || null,
					})),
				});

				if (resp?.success) {
					setStatus("success");
					setShowSuccessModal(true);
					resetForm();
				} else {
					setStatus("error");
				}
				return;
			}

			// ======= CAS INDIVIDUEL =======
			const m = groupMembers[0];
			if (!m?.rsvp_status || !["confirmed", "declined"].includes(m.rsvp_status)) {
				alert("Choisissez une réponse (Je viens / Je ne viens pas).");
				setStatus("error");
				return;
			}

			const payloadIndiv = {
				first_name: m.first_name,
				last_name: m.last_name,
				rsvp_status: m.rsvp_status,
				message: (m.message ?? "").trim() || null,
				attending_pre_event: (m.attending_pre_event === "yes" ? "yes" : "no") as YesNo,
			};

			const response = await fetch(`https://api.les-doudous.com/api/rsvp`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify(payloadIndiv),
			});

			if (response.ok) {
				setStatus("success");
				setShowSuccessModal(true);
				resetForm();
			} else {
				setStatus("error");
			}
		} catch (err) {
			console.error(err);
			setStatus("error");
		}
	};

	const resetForm = () => {
		setQuery("");
		setSelectedGuest(null);
		setGroupMembers([]);
		setGroupType(undefined);
		setGroupMode('none');
		setChildrenPresent('');
		setGroupMessage('');
		setGroupPresence('');
		setGroupPreEvent('');
		setCurrentStep(1);
		setSelectedMemberIndex(null);
	};

	const formCompleted = groupMembers.length > 0 && groupMembers.every(m => {
		// ✅ Vérification stricte : pas de chaînes vides
		const hasValidRSVP = m.rsvp_status === 'confirmed' || m.rsvp_status === 'declined';

		const hasValidPreEvent = m.rsvp_status !== 'confirmed' ||
			(m.attending_pre_event === 'yes' || m.attending_pre_event === 'no');

		return hasValidRSVP && hasValidPreEvent;
	});


	const hasIndividualResponses = groupMembers.length > 1 &&
		groupMembers.some(m => m.rsvp_status === "confirmed" || m.rsvp_status === "declined");

	return (
		<section id="rspv" className="py-16 md:py-24 bg-gradient-to-br from-bordeaux-50 via-white to-gold-50 relative">
			{/* En-tête moderne */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				viewport={{ once: true }}
				className="text-center mb-12"
			>
				<h2 className="heading-secondary mb-4">
					Confirmez votre présence
				</h2>
				<div className="w-32 h-1 bg-gradient-to-r from-bordeaux-600 to-gold-600 mx-auto mb-6 rounded-full"></div>
				<p className="text-xl text-bordeaux-600 max-w-2xl mx-auto leading-relaxed">
					Nous avons hâte de célébrer ce moment magique avec vous !
					<br className="hidden sm:block" />
				</p>
			</motion.div>

			{/* Indicateur d'étapes */}
			<div className="max-w-4xl mx-auto mb-12 px-4">
				<div className="flex items-center justify-center space-x-4">
					{[1, 2, 3].map((step) => (
						<div key={step} className="flex items-center">
							<div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
								currentStep >= step
									? 'bg-bordeaux-600 text-white shadow-lg'
									: 'bg-gray-200 text-gray-500'
							}`}>
								{step}
							</div>
							{step < 3 && (
								<div className={`w-12 h-1 mx-2 rounded-full transition-all duration-300 ${
									currentStep > step ? 'bg-bordeaux-600' : 'bg-gray-200'
								}`} />
							)}
						</div>
					))}
				</div>
				<div className="flex justify-center mt-4 space-x-16 text-sm text-bordeaux-600">
					<span className={currentStep >= 1 ? 'font-semibold' : ''}>Recherche</span>
					<span className={currentStep >= 2 ? 'font-semibold' : ''}>Réponse</span>
					<span className={currentStep >= 3 ? 'font-semibold' : ''}>Validation</span>
				</div>
			</div>

			{/* Formulaire principal */}
			<motion.div
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, delay: 0.2 }}
				viewport={{ once: true }}
				className="max-w-2xl mx-auto px-4"
			>
				<form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-md shadow-2xl border border-white/20 rounded-3xl overflow-hidden">
					{/* Étape 1 : Recherche d'invité */}
					<div className="p-8 md:p-12">
						<div className="flex items-center mb-8 justify-center">
							<div className="w-12 h-12 bg-bordeaux-100 rounded-full flex items-center justify-center mr-4">
								<Search className="w-6 h-6 text-bordeaux-600" />
							</div>
							<div>
								<h3 className="text-2xl text-bordeaux-700">Saisissez votre prénom ou nom de famille</h3>
							</div>
						</div>

						<div className="relative max-w-md mx-auto overflow-visible">
							<div className="relative">
								<input
									type="text"
									value={query}
									onChange={handleInputChange}
									onFocus={() => setShowSuggestions(true)}
									ref={inputRef}
									required
									className="w-full pl-4 pr-4 py-4 border-2 border-bordeaux-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-bordeaux-200 focus:border-bordeaux-500 transition-all duration-200 text-lg text-center bg-white/80 backdrop-blur-sm"
									placeholder="Votre nom ou prénom"
									autoComplete="off"
								/>
								{isLoading && (
									<div className="absolute top-1/2 transform">
										<Loader2 className="w-5 h-5 text-bordeaux-400 animate-spin" />
									</div>
								)}
							</div>

							{/* Suggestions dropdown amélioré */}
							<AnimatePresence>
								{showSuggestions && (
									<motion.div
										initial={{ opacity: 0, y: -10, scale: 0.95 }}
										animate={{ opacity: 1, y: 0, scale: 1 }}
										exit={{ opacity: 0, y: -10, scale: 0.95 }}
										transition={{ duration: 0.2 }}
										className="z-50 left-0 top-full w-full bg-white border-2 border-bordeaux-200 rounded-2xl mt-2 max-h-80 overflow-y-auto shadow-2xl"
									>
										{isLoading && (
											<div className="px-6 py-4 text-bordeaux-400 text-center flex items-center justify-center">
												<Loader2 className="w-4 h-4 mr-2 animate-spin" />
												Recherche en cours...
											</div>
										)}
										{!isLoading && query.length < 2 && (
											<div className="px-6 py-4 text-bordeaux-400 text-center">
												Veuillez entrer au moins 2 lettres
											</div>
										)}
										{!isLoading && query.length >= 2 && suggestions.length === 0 && (
											<div className="px-6 py-4 text-bordeaux-400 text-center flex items-center justify-center">
												<AlertCircle className="w-4 h-4 mr-2" />
												Aucun invité trouvé
											</div>
										)}
										{suggestions.slice(0, 3).map((guest, index) => (
											<motion.div
												key={guest.id}
												initial={{ opacity: 0, x: -10 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{ delay: index * 0.05 }}
												className="px-6 py-4 cursor-pointer hover:bg-bordeaux-50 transition-colors border-b border-bordeaux-100 last:border-b-0 flex items-center justify-between group"
												onClick={() => handleSuggestionClick(guest)}
											>
												<div className="flex items-center">
													<div className="w-10 h-10 bg-bordeaux-100 rounded-full flex items-center justify-center mr-3">
														<User className="w-5 h-5 text-bordeaux-600" />
													</div>
													<div>
														<div className="font-semibold text-bordeaux-700">
															{guest.firstName} {guest.lastName}
														</div>
													</div>
												</div>
												<ChevronRight className="w-5 h-5 text-bordeaux-400 group-hover:text-bordeaux-600 transition-colors" />
											</motion.div>
										))}
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					</div>

					{/* Étape 2 : Choix de réponse */}
					<AnimatePresence>
						{selectedGuest && (
							<motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: 'auto' }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 0.3 }}
								className="border-t-2 border-bordeaux-100 bg-bordeaux-50/50"
							>
								<div className="p-2 md:p-12">
									{/* Mode de réponse pour les groupes */}
									{(groupType === 'family' || groupType === 'couple') && groupMembers.length > 1 && groupMode === 'none' && (
										<div className="text-center mb-8">
											{hasIndividualResponses && (
												<div className="mb-8 p-4 bg-blue-50 rounded-2xl border border-blue-200 max-w-2xl mx-auto">
													<div className="flex items-center justify-center mb-3">
														<AlertCircle className="w-5 h-5 text-blue-600 mr-2" />
														<span className="font-semibold text-blue-700">Réponses déjà enregistrées</span>
													</div>
													<div className="flex flex-wrap justify-center gap-3">
														{groupMembers.map(m => (
															<div key={m.id} className="bg-white px-4 py-2 rounded-xl border border-blue-200 text-sm">
																<span className="font-semibold text-bordeaux-700">{m.first_name} {m.last_name}</span>
																<span className="ml-2 text-blue-600">
																	{m.rsvp_status === "confirmed" ? "✅ Présent" :
																		m.rsvp_status === "declined" ? "❌ Absent" : "❓ En attente"}
																</span>
															</div>
														))}
													</div>
												</div>
											)}

											<div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
												{/* Option réponse groupée - mise en avant */}
												<motion.button
													type="button"
													whileHover={{ scale: 1.02 }}
													whileTap={{ scale: 0.98 }}
													className="relative p-8 bg-gradient-to-r from-bordeaux-600 to-bordeaux-700 text-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-200 overflow-hidden"
													onClick={() => {
														if (hasIndividualResponses) {
															setShowConfirmModal(true)
														} else {
															setGroupMode('grouped');
															setCurrentStep(3);
														}
													}}
												>
													<div className="flex items-center justify-center mb-4">
														<Users className="w-8 h-8" />
													</div>

													<div className="font-bold text-xl mb-3">Je décide pour le groupe</div>
												</motion.button>

												{/* Option réponse individuelle */}
												<motion.button
													type="button"
													whileHover={{ scale: 1.02 }}
													whileTap={{ scale: 0.98 }}
													className="p-8 bg-white border-2 border-bordeaux-200 text-bordeaux-700 rounded-2xl shadow-lg hover:shadow-xl hover:border-bordeaux-300 transition-all duration-200"
													onClick={() => {
														setGroupMode('none');
														setGroupMembers([
															{
																id: selectedGuest.id,
																first_name: selectedGuest.firstName,
																last_name: selectedGuest.lastName,
																rsvp_status: "",
																message: "",
																attending_pre_event: selectedGuest.attending_pre_event || "",
																is_child: selectedGuest.is_child
															}
														]);
														setGroupType('solo');
														setCurrentStep(3);
													}}
												>
													<div className="flex items-center justify-center mb-4">
														<User className="w-10 h-10" />
													</div>

													<div className="font-bold text-xl mb-3">Réponse individuelle</div>
												</motion.button>
											</div>
										</div>
									)}

									{/* Formulaire de réponse individuelle pour chaque membre */}
									{/* Formulaire de réponse groupée (décisionnaire avec sélecteur de membre) */}
									{(groupType === 'family' || groupType === 'couple') && groupMembers.length > 1 && groupMode === 'grouped' && (
										<div className="space-y-6">
											{/* Card principale du décisionnaire */}
											<motion.div
												initial={{ opacity: 0, y: 20 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ duration: 0.3 }}
												className="rounded-2xl border-2  shadow-xl overflow-hidden"
											>
												{/* Corps de la card */}
												<div className="p-6">
													{/* Sélecteur de membre */}
													<div className="mb-6">
														<h4 className="font-semibold text-gold-700 mb-3 text-lg">Séléctionner pour répondre :</h4>
														<div className="flex flex-wrap gap-2">
															{groupMembers.map((member, memberIndex) => {
																const isSelected = selectedMemberIndex === memberIndex;
																const isDecisionMaker = memberIndex === 0;

																return (
																	<button
																		key={member.id}
																		type="button"
																		onClick={() => setSelectedMemberIndex(memberIndex)}
																		className={`px-2 py-1 md:px-4 md:py-3 rounded-xl border-2 transition-all duration-200 flex items-center space-x-2 ${
																			isSelected
																				? (isDecisionMaker
																					? 'border-gold-400 bg-gold-100 text-gold-800'
																					: 'border-bordeaux-400 bg-bordeaux-100 text-bordeaux-800')
																				: 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
																		}`}
																	>
														<span className="font-medium">
															{member.first_name}
														</span>

																		{/* Indicateur de statut */}
																		{member.rsvp_status === 'confirmed' && <span className="text-green-600">✅</span>}
																		{member.rsvp_status === 'declined' && <span className="text-red-600">❌</span>}
																		{!member.rsvp_status && <span className="text-gray-400">❓</span>}
																	</button>
																);
															})}
														</div>
													</div>

													{/* Formulaire pour le membre sélectionné */}
													{selectedMemberIndex !== null && (
														<motion.div
															key={selectedMemberIndex} // Force re-render quand on change de membre
															initial={{ opacity: 0, y: 10 }}
															animate={{ opacity: 1, y: 0 }}
															transition={{ duration: 0.2 }}
															className="bg-white rounded-xl border-2 border-grey-200 p-6"
														>
															{/* Présence le 05 décembre */}
															<div className="mb-6">
																<h6 className="font-semibold text-gray-700 mb-4">Présence le 05 décembre</h6>
																<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
																	{[
																		{ value: 'confirmed', label: `Oui`, icon: '✅', color: 'green' },
																		{ value: 'declined', label: `Non`, icon: '❌', color: 'red' }
																	].map(option => (
																		<label key={option.value} className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
																			groupMembers[selectedMemberIndex].rsvp_status === option.value
																				? `border-${option.color}-400 bg-${option.color}-50 shadow-md`
																				: 'border-gray-200 bg-white hover:border-gold-300 hover:shadow-sm'
																		}`}>
																			<input
																				type="radio"
																				name={`selected-member-presence`}
																				value={option.value}
																				checked={groupMembers[selectedMemberIndex].rsvp_status === option.value}
																				onChange={() => updateMemberRSVP(selectedMemberIndex, option.value)}
																				className="sr-only"
																			/>
																			<span className={`font-medium ${
																				groupMembers[selectedMemberIndex].rsvp_status === option.value ? `text-${option.color}-700` : 'text-gray-700'
																			}`}>
																{option.label}
															</span>
																		</label>
																	))}
																</div>
															</div>

															{/* Présence la veille (si confirmé) */}
															{groupMembers[selectedMemberIndex].rsvp_status === 'confirmed' && (
																<div className="mb-6">
																	<h6 className="font-semibold text-gray-700 mb-4">Présence la veille le 04 décembre</h6>
																	<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
																		{[
																			{ value: 'yes', label: `Oui`, icon: '🌙' },
																			{ value: 'no', label: 'Seulement le 05 décembre', icon: '☀️' }
																		].map(option => (
																			<label key={option.value} className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
																				groupMembers[selectedMemberIndex].attending_pre_event === option.value
																					? 'border-blue-400 bg-blue-50 shadow-md'
																					: 'border-gray-200 bg-white hover:border-gold-300 hover:shadow-sm'
																			}`}>
																				<input
																					type="radio"
																					name={`selected-member-pre-event`}
																					value={option.value}
																					checked={groupMembers[selectedMemberIndex].attending_pre_event === option.value}
																					onChange={() => updateMemberPreEvent(selectedMemberIndex, option.value)}
																					className="sr-only"
																				/>
																				<span className={`font-medium ${
																					groupMembers[selectedMemberIndex].attending_pre_event === option.value ? 'text-blue-700' : 'text-gray-700'
																				}`}>
																	{option.label}
																</span>
																			</label>
																		))}
																	</div>
																</div>
															)}

															{/* Message individuel pour ce membre */}
															{selectedMemberIndex === 0 ? (
																<div>
																	<h6 className="font-semibold text-gray-700 mb-3">Message</h6>
																	<textarea
																		value={groupMembers[selectedMemberIndex].message || ''}
																		onChange={(e) => updateMemberMessage(selectedMemberIndex, e.target.value)}
																		rows={3}
																		className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-300 focus:border-gold-400 resize-none"
																		placeholder="Message pour les mariés..."
																	/>
																</div>
															) : ""}
														</motion.div>
													)}
												</div>
											</motion.div>
										</div>
									)}

									{/* Formulaire pour invité seul */}
									{((groupType === 'solo') || (groupMembers.length === 1)) && (
										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ duration: 0.3 }}
											className="space-y-6"
										>
											{/* Card invité individuel */}
											<div className="card border border-gray-200 shadow-lg">
												<div className="text-center mb-6">
													<h4 className="text-2xl font-bold text-bordeaux-700 mb-2">
														Serez-vous présent(e) le 05 décembre ?
													</h4>
												</div>

												{/* Présence le 05 décembre */}
												<div className="mb-6">
													<div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
														{[
															{ value: 'confirmed', label: 'Oui, je viens !', icon: '✅', color: 'green' },
															{ value: 'declined', label: 'Non, je ne peux pas', icon: '❌', color: 'red' }
														].map(option => (
															<label key={option.value} className={`flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
																groupMembers[0]?.rsvp_status === option.value
																	? `border-${option.color}-400 bg-${option.color}-50 shadow-md`
																	: 'border-gray-200 bg-white hover:border-bordeaux-300 hover:shadow-sm'
															}`}>
																<input
																	type="radio"
																	name="solo-presence"
																	value={option.value}
																	checked={groupMembers[0]?.rsvp_status === option.value}
																	onChange={(e) => updateMemberRSVP(0, e.target.value)}
																	className="sr-only"
																/>
																<span className={`font-medium ${
																	groupMembers[0]?.rsvp_status === option.value ? `text-${option.color}-700` : 'text-gray-700'
																}`}>
																	{option.label}
																</span>
															</label>
														))}
													</div>
												</div>

												{/* Présence la veille (si confirmé) */}
												{groupMembers[0]?.rsvp_status === 'confirmed' && (
													<div className="mb-6">
														<h6 className="font-semibold text-gray-700 mb-4 text-center">
															Serez-vous présent(e) la veille le 04 décembre ?
														</h6>
														<div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
															{[
																{ value: 'yes', label: 'Oui', icon: '🌙' },
																{ value: 'no', label: 'Seulement le 05 décembre', icon: '☀️' }
															].map(option => (
																<label key={option.value} className={`flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
																	groupMembers[0]?.attending_pre_event === option.value
																		? 'border-blue-400 bg-blue-50 shadow-md'
																		: 'border-gray-200 bg-white hover:border-bordeaux-300 hover:shadow-sm'
																}`}>
																	<input
																		type="radio"
																		name="solo-pre-event"
																		value={option.value}
																		checked={groupMembers[0]?.attending_pre_event === option.value}
																		onChange={(e) => updateMemberPreEvent(0, e.target.value)}
																		className="sr-only"
																	/>
																	<span className={`font-medium ${
																		groupMembers[0]?.attending_pre_event === option.value ? 'text-blue-700' : 'text-gray-700'
																	}`}>
																		{option.label}
																	</span>
																</label>
															))}
														</div>
													</div>
												)}

												{/* Message */}
												<div>
													<h6 className="font-semibold text-gray-700 mb-3 text-center">
														Un message pour les mariés ?
													</h6>
													<textarea
														value={groupMembers[0]?.message || ''}
														onChange={(e) => updateMemberMessage(0, e.target.value)}
														rows={3}
														className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-bordeaux-300 focus:border-bordeaux-400 resize-none"
														placeholder="Votre message pour Julia & Benjamin..."
													/>
												</div>
											</div>
										</motion.div>
									)}

									{/* Bouton de soumission */}
									{(groupType === 'solo' || groupMembers.length === 1 || groupMode === 'grouped') && (
										<motion.div
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											transition={{ delay: 0.3 }}
											className="text-center mt-8"
										>
											<button
												type="submit"
												disabled={status === 'success' || !formCompleted}
												className={`px-8 py-4 text-lg font-semibold rounded-2xl transition-all duration-200 shadow-lg ${
													formCompleted
														? 'bg-gradient-to-r from-bordeaux-600 to-bordeaux-700 text-white hover:from-bordeaux-700 hover:to-bordeaux-800 hover:shadow-xl transform hover:scale-105'
														: 'bg-gray-200 text-gray-500 cursor-not-allowed'
												}`}
											>
												{status === 'success' ? '✅ Réponse enregistrée' : 'Confirmer ma réponse'}
											</button>
										</motion.div>
									)}
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</motion.div>

			{/* Modal de confirmation pour écraser les réponses */}
			<ConfirmModal
				isOpen={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onConfirm={() => {
					setShowConfirmModal(false);
					setGroupMode('grouped');
					setCurrentStep(3);
				}}
				title="Modifier les réponses existantes"
				message="Des réponses ont déjà été enregistrées pour ce groupe. Souhaitez-vous les modifier ?"
			/>

			{/* Modal de succès */}
			<AnimatePresence>
				{showSuccessModal && (
					<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
					onClick={() => setShowSuccessModal(false)}
				>
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.9 }}
						className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
						onClick={(e) => e.stopPropagation()}
					>
						<h3 className="text-2xl font-bold text-bordeaux-700 mb-4">
							Merci !
						</h3>
						<p className="text-bordeaux-600 mb-6">
							Votre réponse a bien été enregistrée.
							<br/>
							Nous avons hâte de vous voir !
						</p>

						{/* Section cagnotte */}
						<div className="bg-gold-50 rounded-xl p-4 mb-6 border border-gold-200">
							<p className="text-gold-600 text-sm mb-3">
								Avec votre participation,
								vous serez avec nous dans nos plus beaux souvenirs
							</p>
							<a
								href="#cagnotte"
								onClick={() => {
									setShowSuccessModal(false);
									// Scroll vers la section cagnotte après fermeture du modal
									setTimeout(() => {
										const cagnotteSection = document.getElementById('cagnotte');
										if (cagnotteSection) {
											cagnotteSection.scrollIntoView({ behavior: 'smooth' });
										}
									}, 300);
								}}
								className="inline-flex items-center px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors text-sm font-medium"
							>
								Découvrir la cagnotte
							</a>
						</div>

						<button
							onClick={() => setShowSuccessModal(false)}
							className="btn-primary w-full"
						>
							Fermer
						</button>
					</motion.div>
				</motion.div>
				)}
			</AnimatePresence>
		</section>
	);
};

export default RSVPForm;
